import { useIsAmbientCopilotEnabled } from '@/features/flags';
import { useSectionsTelemetryContext } from '@/hooks/useMarkdownSectionalEditorFlagsTelemetry';
import { isProductFeedbackAvailable } from '@/utils/heidiReach';
import dynamic from 'next/dynamic';

import { AfterFirstPaint } from './after-first-paint';

const AgentEventBus = dynamic(
  () =>
    import('@/features/agent-events/components/agent-event-bus').then(
      (mod) => mod.AgentEventBus
    ),
  { ssr: false }
);

const RustExecutorTokenRefreshRuntime = dynamic(
  () =>
    import('@/features/rpa/agent-client/rust-executor-token-refresh').then(
      (mod) => mod.RustExecutorTokenRefreshRuntime
    ),
  { ssr: false }
);

const ProjectBackgroundAppBridge = dynamic(
  () =>
    import('@/utils/work-home-ports-wiring').then(() =>
      import('@/features/projects/components/ProjectBackgroundAppBridge').then(
        (mod) => mod.ProjectBackgroundAppBridge
      )
    ),
  { ssr: false }
);

const BrazeIamController = dynamic(
  () =>
    import('@/components/braze/BrazeIamController').then((mod) => mod.BrazeIamController),
  { ssr: false }
);

const BulkSyncWidget = dynamic(
  () => import('@/components/bulk-sync').then((mod) => mod.BulkSyncWidget),
  { ssr: false }
);

const CannyIdentify = dynamic(
  () => import('@/components/canny').then((mod) => mod.CannyIdentify),
  { ssr: false }
);

const MiniMode = dynamic(
  () => import('@/components/mini-mode').then((mod) => mod.MiniMode),
  { ssr: false }
);

const SessionRecordingOverlay = dynamic(
  () =>
    import('@/utils/work-home-ports-wiring').then(() =>
      import('@heidi/agents').then((mod) => mod.SessionRecordingOverlay)
    ),
  { ssr: false }
);

const GlobalVoiceAssistant = dynamic(
  () => import('@/features/voice-assistant').then((mod) => mod.GlobalVoiceAssistant),
  { ssr: false }
);

const AppointmentNotificationsController = dynamic(
  () =>
    import('./appointment-notifications/components/appointment-notifications-controller').then(
      (mod) => mod.AppointmentNotificationsController
    ),
  { ssr: false }
);

const AmbientHeidiHost = dynamic(
  () => import('./ambient-heidi-host').then((mod) => mod.AmbientHeidiHost),
  { ssr: false }
);

const WorkSurfaceShortcutListeners = dynamic(
  () =>
    import('@/features/projects/components/WorkSurfaceShortcutListeners').then(
      (mod) => mod.WorkSurfaceShortcutListeners
    ),
  { ssr: false }
);

const CopilotIntentHost = dynamic(
  () =>
    import('@/utils/work-home-ports-wiring').then(() =>
      import('@/features/copilot/copilot-intent-host').then(
        (mod) => mod.CopilotIntentHost
      )
    ),
  { ssr: false }
);

// Always mounted (not gated) so it can hide the copilot-overlay window the
// instant the flag flips OFF — see `useIsAmbientCopilotEnabled`.
const CopilotWindowGate = dynamic(
  () =>
    import('@/features/copilot/copilot-window-gate').then((mod) => mod.CopilotWindowGate),
  { ssr: false }
);

const CopilotActiveNotePublisher = dynamic(
  () =>
    import('@/features/copilot/copilot-active-note-publisher').then(
      (mod) => mod.CopilotActiveNotePublisher
    ),
  { ssr: false }
);

// Serves the agent's `invoke_ax_quick_action` over the Rust bridge. Always
// mounted (desktop self-gates): the Rust build advertises the capability, so
// serving must not depend on a frontend flag.
const QuickActionHost = dynamic(
  () => import('@/features/copilot/quick-action-host').then((mod) => mod.QuickActionHost),
  { ssr: false }
);

const CopilotPrechartOrchestrator = dynamic(
  () =>
    import('@/utils/work-home-ports-wiring').then(() =>
      import('@/features/copilot/copilot-prechart-orchestrator').then(
        (mod) => mod.CopilotPrechartOrchestrator
      )
    ),
  { ssr: false }
);

const CopilotPrechartContextInsertionHost = dynamic(
  () =>
    import('@/features/copilot/copilot-prechart-context-insertion-host').then(
      (mod) => mod.CopilotPrechartContextInsertionHost
    ),
  { ssr: false }
);

// Headless desktop-only adapter (APP-6259): mirrors the Rust backend-
// reachability monitor into offlineConnectivityStateAtom. No-op on web.
const OfflineConnectivityProvider = dynamic(
  () =>
    import('@/providers/offline-connectivity-provider').then(
      (mod) => mod.OfflineConnectivityProvider
    ),
  { ssr: false }
);

// pyne-provider uses a default export, unlike the named widget modules above.
const Pyne = dynamic(() => import('@/providers/pyne-provider'), {
  ssr: false,
});

const RemoteStatusCard = dynamic(
  () =>
    import('@/features/chronicle/components/remote-status-card').then(
      (mod) => mod.RemoteStatusCard
    ),
  { ssr: false }
);

// The host binds the atoms, the entitlement reads, the analytics emit and the
// authenticated-source access seam. It stays behind this `dynamic` because one
// of its imports (`getAuthTokenForHttpClientSync`) reaches the app store.
const SourceViewerSheet = dynamic(
  () => import('./source-viewer-sheet-host').then((mod) => mod.SourceViewerSheetHost),
  { ssr: false }
);

const LabsExperimentalTermsIdlePrompt = dynamic(
  () =>
    import('@/features/settings/heidi-labs/terms/labs-experimental-terms-idle-prompt').then(
      (mod) => mod.LabsExperimentalTermsIdlePrompt
    ),
  { ssr: false }
);

// Widgets that live above ChronicleStateProvider on main. Kept here so the
// deferred set mirrors the original tree shape — relocating them under
// ChronicleStateProvider would couple their lifetime to it.
export const DeferredAppShellWidgets = () => {
  const isAmbientCopilotEnabled = useIsAmbientCopilotEnabled();

  // App-shell mount so the cohort stamp doesn't depend on an editor rendering.
  useSectionsTelemetryContext();

  return (
    <AfterFirstPaint>
      <AppointmentNotificationsController />
      <OfflineConnectivityProvider />
      <MiniMode />
      <BrazeIamController />
      {isProductFeedbackAvailable && <CannyIdentify />}
      <AgentEventBus />
      <RustExecutorTokenRefreshRuntime />
      <ProjectBackgroundAppBridge />
      <SessionRecordingOverlay />
      <GlobalVoiceAssistant />
      <AmbientHeidiHost />
      {/* Routine shortcuts: one app-wide mount so they fire on Scribe sessions
          and Work, not only /scribe/chat/projects. */}
      <WorkSurfaceShortcutListeners />
      <LabsExperimentalTermsIdlePrompt />
      <CopilotWindowGate />
      <QuickActionHost />
      <CopilotPrechartOrchestrator />
      <CopilotPrechartContextInsertionHost />
      {isAmbientCopilotEnabled && (
        <>
          <CopilotIntentHost />
          <CopilotActiveNotePublisher />
        </>
      )}
    </AfterFirstPaint>
  );
};

// Widgets that live inside ChronicleStateProvider on main. BulkSyncWidget
// reads Chronicle atoms; SourceViewerSheet has historically been mounted next
// to the route component; Pyne is a <Script> tag that's position-agnostic but
// kept here to match its pre-PR location.
export const DeferredChronicleWidgets = ({
  isPyneEnabled,
}: {
  isPyneEnabled: boolean;
}) => {
  return (
    <AfterFirstPaint>
      <BulkSyncWidget />
      {/* Same corner + z-[90] convention as BulkSyncWidget; sonner toasts sit
          bottom-center so the two layers never overlap. Card self-gates. */}
      <div className="fixed bottom-3 end-3 z-[90]">
        <RemoteStatusCard />
      </div>
      {isPyneEnabled && <Pyne />}
      <SourceViewerSheet />
    </AfterFirstPaint>
  );
};
